import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { useDispatch, useSelector } from 'react-redux';
import * as S from '~/pages/Match/styles';
import { Icon } from '~/components';
import { getSelectedMatchScoreRequest } from '~/store/modules/matches/actions';

export default function MatchHeader() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { data: match } = useSelector(state => state.matches.selectedMatch);
  const {
    data: { homeScore, awayScore },
  } = useSelector(state => state.matches.selectedMatchScore);
  const {
    data: { matchIsBeingPlayed },
  } = useSelector(state => state.matches.matchStatus);

  useEffect(() => {
    const fetchEvents = () => {
      const shouldFetch = matchIsBeingPlayed || matchIsBeingPlayed === null;
      if (match) {
        if (shouldFetch) {
          dispatch(getSelectedMatchScoreRequest(match.id));
        }
      }
    };

    fetchEvents();
    const interval = setInterval(fetchEvents, 60000);

    return () => {
      clearInterval(interval);
    };
  }, [dispatch, match, matchIsBeingPlayed]);

  const returnScore = team => {
    if (team === 'home' && homeScore) {
      return homeScore;
    }
    if (team === 'home' && !homeScore) {
      return match ? match.homeScore : `-`;
    }
    if (team === 'away' && awayScore) {
      return awayScore;
    }
    if (team === 'away' && !awayScore) {
      return match ? match.awayScore : `-`;
    }
    return '-';
  };

  return (
    <S.HeaderContainer>
      <S.MainHeader competition={match?.competitionId}>
        <S.GoBackButton to="/dashboard" competition={match?.competitionId}>
          <Icon name="chevronLeft" size={15} color="black" />
          <S.ButtonLabel>
            {t('goBackTo')} <br />
            <span>{t('Dashboard')?.toUpperCase()}</span>
          </S.ButtonLabel>
        </S.GoBackButton>

        <S.HeaderItem>
          <Icon name="stopwatchWithBall" size={20} color="white" />
          <S.HeaderItemLabel>
            <span>{match ? match.gameweek.week : '-'}</span>
            <br />
            {t('round')}
          </S.HeaderItemLabel>
        </S.HeaderItem>

        <S.HeaderItem>
          <Icon name="calendar" size={20} color="white" />
          <S.HeaderItemLabel>
            <span>
              {match ? format(new Date(match.date), 'd MMM') : `-- ---`}
            </span>
            <br />
            {match ? format(new Date(match.date), 'YYY') : `----`}
          </S.HeaderItemLabel>
        </S.HeaderItem>

        <S.HeaderItem>
          <Icon name="stopwatch" size={20} color="white" />
          <S.HeaderItemLabel>
            <span>
              {match ? `${match.localTime ? match.localTime : '-'}` : '--:--'}
            </span>
            <br />
            {t('local')}
          </S.HeaderItemLabel>
          {match && match.inCanary && (
            <S.HeaderItemLabel>
              <span>
                {match
                  ? `${match.peninsulaTime ? match.peninsulaTime : '-'}`
                  : `--:--`}
              </span>
              <br />
              {t('peninsular')}
            </S.HeaderItemLabel>
          )}
        </S.HeaderItem>

        <S.HeaderItem>
          <Icon name="field" size={20} color="white" />
          <S.HeaderItemLabel>
            <span>
              {match ? `${match.venue ? match.venue.name : '-'}` : `-`}
            </span>
            <br />
            {t('stadium')}
          </S.HeaderItemLabel>
        </S.HeaderItem>

        <S.HeaderItem>
          <Icon name="weather" size={20} color="white" />
          <S.HeaderItemLabel>
            <span>
              {match
                ? `${
                    match.matchGrass
                      ? `${
                          match.matchGrass.weatherForecastRainfall
                            ? match.matchGrass.weatherForecastRainfall
                            : '-'
                        } mm/m2`
                      : '-'
                  }`
                : '-'}
            </span>
            <br />
            {t('rainfall')}
          </S.HeaderItemLabel>
        </S.HeaderItem>

        <S.HeaderItem>
          <Icon name="humidity" size={20} color="white" />
          <S.HeaderItemLabel>
            <span>
              {match
                ? `${
                    match.matchGrass
                      ? `${
                          match.matchGrass.weatherForecastHumidity
                            ? match.matchGrass.weatherForecastHumidity
                            : '-'
                        } %`
                      : '-'
                  }`
                : '-'}
            </span>
            <br />
            {t('humidity')}
          </S.HeaderItemLabel>
        </S.HeaderItem>

        <S.HeaderItem>
          <Icon name="temperature" size={20} color="white" />
          <S.HeaderItemLabel>
            <span>
              {match
                ? `${
                    match.matchGrass
                      ? `${
                          match.matchGrass.weatherForecastTemperature
                            ? match.matchGrass.weatherForecastTemperature
                            : '-'
                        } °C`
                      : '-'
                  }`
                : '-'}
            </span>
            <br />
            {t('temperature')}
          </S.HeaderItemLabel>
        </S.HeaderItem>
        <S.HeaderItemLogo>
          <img
            src={
              match
                ? match.competitionLogo
                : 'https://storageprodmzapp.blob.core.windows.net/assets/logos/laliga-monocromatic-negative-h.png'
            }
            alt="championship"
          />
        </S.HeaderItemLogo>
      </S.MainHeader>
      <S.MatchScore>
        <S.TeamScore type="local">
          <S.TeamContainer type="local">
            {match ? match.homeTeam.nickname : ''}
            {match && (
              <img src={match ? match.homeTeam.shieldImageUrl : ''} alt=" " />
            )}
          </S.TeamContainer>
          <span>{returnScore('home')}</span>
        </S.TeamScore>
        <S.TeamScore>
          <S.TeamContainer>
            {match ? match.awayTeam.nickname : ``}
            {match && (
              <img src={match ? match.awayTeam.shieldImageUrl : ``} alt=" " />
            )}
          </S.TeamContainer>
          <span>{returnScore('away')}</span>
        </S.TeamScore>
      </S.MatchScore>
    </S.HeaderContainer>
  );
}
